import _ from 'lodash';

export function millisecondsToSeconds(value) {
  const parsedValue = parseFloat(value);
  return (value != null && _.isNumber(parsedValue) && !_.isNaN(parsedValue)) ?
    parsedValue / 1000 : value;
}

export function secondsToMilliseconds(value) {
  const parsedValue = parseFloat(value);
  if (value == null) {
    return '';
  }
  return (value != null && _.isNumber(parsedValue) && !_.isNaN(parsedValue)) ?
    parsedValue * 1000 : value;
}

export function inputConvertToBooleanValue(value) {
  return !!value;
}

export function inputConvertToSafeValue(value) {
  return value != null ? value : '';
}

export const PASSENGER_BEHAVIOURS = {
  MANUAL: '',
  ALWAYS_BOOK: 'ALWAYS_BOOK',
  ALWAYS_CANCEL: 'ALWAYS_CANCEL',
};

export const PASSENGER_BEHAVIOURS_NAMES = {
  [PASSENGER_BEHAVIOURS.MANUAL]: 'Manual',
  [PASSENGER_BEHAVIOURS.ALWAYS_BOOK]: 'Always Book',
  [PASSENGER_BEHAVIOURS.ALWAYS_CANCEL]: 'Always Cancel',
};

export const PASSENGER_BEHAVIOUR_TYPES = [
  {
    name: PASSENGER_BEHAVIOURS_NAMES[PASSENGER_BEHAVIOURS.MANUAL],
    value: PASSENGER_BEHAVIOURS.MANUAL,
  },
  {
    name: PASSENGER_BEHAVIOURS_NAMES[PASSENGER_BEHAVIOURS.ALWAYS_BOOK],
    value: PASSENGER_BEHAVIOURS.ALWAYS_BOOK,
  },
  {
    name: PASSENGER_BEHAVIOURS_NAMES[PASSENGER_BEHAVIOURS.ALWAYS_CANCEL],
    value: PASSENGER_BEHAVIOURS.ALWAYS_CANCEL,
  },
];

export function hasPassengerBehaviour(behaviourType) {
  return !!behaviourType && behaviourType !== PASSENGER_BEHAVIOURS.MANUAL;
}

const bookingInterval = {
  name: 'bookingInterval',
  type: 'number',
  hint: 'Interval (sec) between creating bookings. Min: 1s',
  renderValue: millisecondsToSeconds,
  processValue: secondsToMilliseconds,
  validate(value) {
    const parsedValue = parseFloat(value);
    return _.isFinite(parsedValue) && parsedValue >= 1.0;
  },
};

const pickUpLatitude = {
  name: 'pickUpLatitude',
  type: 'number',
  hint: 'Pick Up Lat. Required',
  renderValue: inputConvertToSafeValue,
  processValue: parseFloat,
  validate: value => value != null && value !== '',
};

const pickUpLongitude = {
  name: 'pickUpLongitude',
  type: 'number',
  hint: 'Pick Up Lon. Required',
  renderValue: inputConvertToSafeValue,
  processValue: parseFloat,
  validate: value => value != null && value !== '',
};

const dropOffLatitude = {
  name: 'dropOffLatitude',
  type: 'number',
  hint: 'Drop Off Lat. Required',
  renderValue: inputConvertToSafeValue,
  processValue: parseFloat,
  validate: value => value != null && value !== '',
};

const dropOffLongitude = {
  name: 'dropOffLongitude',
  type: 'number',
  hint: 'Drop Off Lon. Required',
  renderValue: inputConvertToSafeValue,
  processValue: parseFloat,
  validate: value => value != null && value !== '',
};

const taxiTypeID = {
  name: 'taxiTypeID',
  type: 'string',
  hint: 'Taxi Type ID. Required. Must be a number',
  renderValue: inputConvertToSafeValue,
  processValue: _.identity,
  validate: value => /^\d+$/.test(value),
};

export const PASSENGER_BEHAVIOUR_PARAMS = {
  [PASSENGER_BEHAVIOURS.MANUAL]: [],
  [PASSENGER_BEHAVIOURS.ALWAYS_BOOK]: [
    bookingInterval,
    pickUpLatitude,
    pickUpLongitude,
    dropOffLatitude,
    dropOffLongitude,
    taxiTypeID,
  ],
  [PASSENGER_BEHAVIOURS.ALWAYS_CANCEL]: [
    bookingInterval,
    pickUpLatitude,
    pickUpLongitude,
    dropOffLatitude,
    dropOffLongitude,
    taxiTypeID,
  ],
};

export const DRIVER_BEHAVIOURS = {
  MANUAL: '',
  ALWAYS_BID: 'ALWAYS_BID',
  ALWAYS_DECLINE: 'ALWAYS_DECLINE',
  ALWAYS_CANCEL: 'ALWAYS_CANCEL',
};

export const DRIVER_BEHAVIOURS_NAMES = {
  [DRIVER_BEHAVIOURS.MANUAL]: 'Manual',
  [DRIVER_BEHAVIOURS.ALWAYS_BID]: 'Always Bid',
  [DRIVER_BEHAVIOURS.ALWAYS_DECLINE]: 'Always Decline',
  [DRIVER_BEHAVIOURS.ALWAYS_CANCEL]: 'Always Cancel',
};

export const DRIVER_BEHAVIOUR_TYPES = [
  {
    name: DRIVER_BEHAVIOURS_NAMES[DRIVER_BEHAVIOURS.MANUAL],
    value: DRIVER_BEHAVIOURS.MANUAL,
  },
  {
    name: DRIVER_BEHAVIOURS_NAMES[DRIVER_BEHAVIOURS.ALWAYS_BID],
    value: DRIVER_BEHAVIOURS.ALWAYS_BID,
  },
  {
    name: DRIVER_BEHAVIOURS_NAMES[DRIVER_BEHAVIOURS.ALWAYS_DECLINE],
    value: DRIVER_BEHAVIOURS.ALWAYS_DECLINE,
  },
  {
    name: DRIVER_BEHAVIOURS_NAMES[DRIVER_BEHAVIOURS.ALWAYS_CANCEL],
    value: DRIVER_BEHAVIOURS.ALWAYS_CANCEL,
  },
];

export function hasDriverBehaviour(behaviourType) {
  return !!behaviourType && behaviourType !== DRIVER_BEHAVIOURS.MANUAL;
}

const loiterRadius = {
  name: 'loiterRadius',
  type: 'number',
  hint: 'The radius in KM that the driver will loiter around the centre',
  renderValue: inputConvertToSafeValue,
  processValue: parseFloat,
};

export const DRIVER_BEHAVIOUR_PARAMS = {
  [DRIVER_BEHAVIOURS.MANUAL]: [
    loiterRadius,
  ],
  [DRIVER_BEHAVIOURS.ALWAYS_BID]: [
    loiterRadius,
    {
      name: 'pickUpDelay',
      type: 'number',
      hint: 'Delay (sec) between awarded and picking up',
      renderValue: millisecondsToSeconds,
      processValue: secondsToMilliseconds,
    },
    {
      name: 'sendImHereNotification',
      type: 'boolean',
      renderValue: millisecondsToSeconds,
      processValue: inputConvertToBooleanValue,
    },
    {
      name: 'dropOffDelay',
      type: 'number',
      hint: 'Delay (sec) between picking up and dropping off',
      renderValue: millisecondsToSeconds,
      processValue: secondsToMilliseconds,
    },
    {
      name: 'completeJobDelay',
      type: 'number',
      hint: 'Delay (sec) between dropping off and completing the job',
      renderValue: millisecondsToSeconds,
      processValue: secondsToMilliseconds,
    },
  ],
  [DRIVER_BEHAVIOURS.ALWAYS_DECLINE]: [
    loiterRadius,
  ],
  [DRIVER_BEHAVIOURS.ALWAYS_CANCEL]: [
    loiterRadius,
    {
      name: 'cancelDelay',
      type: 'number',
      hint: 'Delay (sec) between awarded and cancelling the job',
      renderValue: millisecondsToSeconds,
      processValue: secondsToMilliseconds,
    },
  ],
};

export const DEFAULT_PAYMENT_TYPE = 'CASH';

export const PAYMENT_TYPES = [
  {
    name: 'CASH',
    display: 'Cash',
  },
  {
    name: 'CREDIT',
    display: 'Credit',
  },
  {
    name: 'VISA',
    display: 'Visa',
  },
  {
    name: 'MASTERCARD',
    display: 'MasterCard',
  },
  {
    name: 'ALIPAY',
    display: 'Alipay',
  },
  {
    name: 'Android Pay',
    display: 'Android Pay',
  },
  {
    name: 'PayLater',
    display: 'PayLater',
  },
];

export const REWARD_TYPES = [
  {
    name: 'promotionCode',
    display: 'Promo Code',
  },
  {
    name: 'rewardID',
    display: 'Reward ID',
  },
];

export const DEFAULT_REWARD_TYPE = REWARD_TYPES[0].name;



/** WEBPACK FOOTER **
 ** ./src/js/utils/library-utils.js
 **/